import Chapter from "./Chapter";
import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "@/components/ui/sonner";
import { ArrowRight, CheckCircle2, Loader2, Clock, Headset, ShieldCheck, Phone } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initial = {
  prenom: "",
  nom: "",
  organisation: "",
  email: "",
  telephone: "",
  effectif: "",
  message: "",
  website: "",
};

export default function ContactForm() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.prenom || !form.nom || !form.organisation || !form.email) {
      toast.error("Merci de renseigner les champs obligatoires.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      toast.dismiss();
      setDone(true);
      toast.success("Demande envoyée !", {
        description: "Notre équipe vous recontacte sous 24h ouvrées.",
      });
      setForm(initial);
    } catch (err) {
      toast.error("Une erreur est survenue", {
        description: "Veuillez réessayer ou nous écrire à contact@sapeur-360.fr",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 lg:py-32 bg-[#FAFAFA]">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          {/* Left selling text */}
          <div>
            <Chapter index={7} label="Demander une démonstration" />
            <h2 className="font-display mt-4 text-3xl md:text-4xl font-bold tracking-tight">
              Voyez Sapeur-360 en action sur vos véhicules.
            </h2>
            <p className="mt-4 text-lg text-[#52525B] max-w-md">
              Échangez avec un de nos experts. Démonstration personnalisée sur votre site ou en visio,
              adaptée à votre métier (pompiers, fourrière, garage, concession) et sans engagement.
            </p>

            <ul className="mt-8 space-y-4">
              {[
                { icon: Clock, t: "Réponse sous 24h ouvrées", d: "Un créneau de démo proposé rapidement." },
                { icon: Headset, t: "Un interlocuteur expert", d: "Un spécialiste produit, pas un commercial." },
                { icon: ShieldCheck, t: "Démonstration sur vos cas", d: "Sur site ou en visio, sur vos véhicules." },
              ].map((it) => (
                <li key={it.t} className="flex gap-4">
                  <span className="grid h-11 w-11 place-items-center bg-white border border-zinc-200 rounded-sm shrink-0">
                    <it.icon className="h-5 w-5 text-[#E63946]" />
                  </span>
                  <div>
                    <div className="font-bold">{it.t}</div>
                    <div className="text-sm text-[#52525B]">{it.d}</div>
                  </div>
                </li>
              ))}
            </ul>

            <a
              data-testid="contact-phone"
              href="tel:+33780179467"
              className="mt-8 inline-flex items-center gap-3 bg-[#0A0A0A] hover:bg-[#E63946] text-white rounded-sm px-5 py-4 transition-colors duration-300 group"
            >
              <span className="grid h-10 w-10 place-items-center bg-white/10 rounded-sm shrink-0">
                <Phone className="h-5 w-5" />
              </span>
              <span>
                <span className="block font-mono-tech text-[11px] uppercase tracking-wider text-white/60 group-hover:text-white/80">Ou appelez-nous directement</span>
                <span className="block font-display text-lg font-bold tracking-tight">+33 (0)7 80 17 94 67</span>
              </span>
            </a>
          </div>

          {/* Right form */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.5 }}
            className="bg-white border border-zinc-200 rounded-sm p-7 lg:p-9 shadow-sm"
          >
            {done ? (
              <div data-testid="contact-success" className="text-center py-12">
                <CheckCircle2 className="h-14 w-14 text-[#10B981] mx-auto" />
                <h3 className="font-display mt-5 text-2xl font-bold tracking-tight">Demande bien reçue.</h3>
                <p className="mt-2 text-[#52525B]">
                  Merci ! Notre équipe vous recontacte très vite pour planifier votre démonstration.
                </p>
                <button
                  data-testid="contact-reset"
                  onClick={() => setDone(false)}
                  className="mt-6 text-sm font-bold text-[#E63946] hover:text-[#BE123C]"
                >
                  Envoyer une autre demande
                </button>
              </div>
            ) : (
              <form data-testid="contact-form" onSubmit={submit} className="space-y-5">
                {/* Honeypot anti-spam — hidden from humans, filled by bots */}
                <input
                  type="text"
                  name="website"
                  value={form.website}
                  onChange={update}
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
                />
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Prénom *" name="prenom" value={form.prenom} onChange={update} placeholder="Julien" testid="input-prenom" />
                  <Field label="Nom *" name="nom" value={form.nom} onChange={update} placeholder="Marchand" testid="input-nom" />
                </div>
                <Field label="Société / Organisation *" name="organisation" value={form.organisation} onChange={update} placeholder="Garage Martin / SDIS 33 / Fourrière…" testid="input-organisation" />
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Email professionnel *" name="email" type="email" value={form.email} onChange={update} placeholder="vous@societe.fr" testid="input-email" />
                  <Field label="Téléphone" name="telephone" value={form.telephone} onChange={update} placeholder="06 12 34 56 78" testid="input-telephone" />
                </div>
                <div>
                  <label className="block text-sm font-bold mb-1.5">Secteur d'activité</label>
                  <select
                    data-testid="input-effectif"
                    name="effectif"
                    value={form.effectif}
                    onChange={update}
                    className="w-full rounded-sm border border-zinc-300 bg-white px-3.5 py-2.5 text-sm focus:outline-none focus:border-[#0A0A0A] transition-colors"
                  >
                    <option value="">Sélectionner…</option>
                    <option>Sapeurs-pompiers / SDIS</option>
                    <option>Service de fourrière / dépannage</option>
                    <option>Garage / carrosserie</option>
                    <option>Concession / showroom</option>
                    <option>Gestion de parking</option>
                    <option>Autre</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-bold mb-1.5">Votre besoin</label>
                  <textarea
                    data-testid="input-message"
                    name="message"
                    value={form.message}
                    onChange={update}
                    rows={3}
                    placeholder="Décrivez brièvement votre contexte et vos attentes…"
                    className="w-full rounded-sm border border-zinc-300 bg-white px-3.5 py-2.5 text-sm focus:outline-none focus:border-[#0A0A0A] transition-colors resize-none"
                  />
                </div>

                <button
                  data-testid="contact-form-submit"
                  type="submit"
                  disabled={loading}
                  className="group w-full inline-flex items-center justify-center gap-2 bg-[#E63946] hover:bg-[#BE123C] disabled:opacity-70 text-white font-bold px-6 py-3.5 rounded-sm transition-colors duration-300"
                >
                  {loading ? (
                    <><Loader2 className="h-4 w-4 animate-spin" /> Envoi en cours…</>
                  ) : (
                    <>Demander ma démonstration <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" /></>
                  )}
                </button>
                <p className="text-xs text-[#A1A1AA] text-center">
                  En soumettant ce formulaire, vous acceptez d'être recontacté par Sapeur-360.
                </p>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, name, value, onChange, placeholder, type = "text", testid }) {
  const emailProps =
    type === "email"
      ? {
          onInvalid: (e) => e.target.setCustomValidity("Veuillez saisir une adresse email valide."),
          onInput: (e) => e.target.setCustomValidity(""),
        }
      : {};
  return (
    <div>
      <label className="block text-sm font-bold mb-1.5">{label}</label>
      <input
        data-testid={testid}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        {...emailProps}
        className="w-full rounded-sm border border-zinc-300 bg-white px-3.5 py-2.5 text-sm focus:outline-none focus:border-[#0A0A0A] transition-colors"
      />
    </div>
  );
}
